import React, { useState } from 'react'
import moment from 'moment'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import {
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Paper,
} from '@mui/material'
import BigModal from '../../../../components/Modals/BigModal'
import AccessLogs from './AccessLogs'
import DeleteRecord from './DeleteRecord'
import ReassignRecord from './ReassignRecord'
import RaiseAuditDialog from '../../audits/RaiseAuditDialog'
import Age from '../../../../components/Age'
import { Info, Schedule } from '@mui/icons-material'
import SmallModal from '../../../../components/Modals/SmallModal'
import { DetailRow } from '../../../../components/Cards/DetailsBox'
import UpgradeLock from '../../../../assets/icons/UpgradeLock'

const Header = ({ record, loadRecord, handlePDFDownload }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [showAccessLogs, setShowAccessLogs] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [openReassign, setOpenReassign] = useState(false)
  const [openTimeInfo, setOpenTimeInfo] = useState(false)
  const [openRaiseAudit, setOpenRaiseAudit] = useState(false)

  const treatmentLocation = record?.review?.currentTreatmentLocation
  const currentAuthor =
    record?.review?.currentOwner ??
    (treatmentLocation ? null : record?.review?.completedBy)
  const currentHolderName =
    treatmentLocation?.name ||
    [currentAuthor?.firstName, currentAuthor?.lastName].filter(Boolean).join(' ')

  // The frozen snapshot outlives the live account — a subcontractor's access
  // lapsing (or an account being archived) must not blank the original author
  const completedByRecord = record?.review?.completedByRecord
  const completedByName =
    completedByRecord?.name ||
    [
      record?.review?.completedBy?.firstName,
      record?.review?.completedBy?.lastName,
    ]
      .filter(Boolean)
      .join(' ')
  const completedByDetail = [
    completedByRecord?.clinicalRole,
    completedByRecord?.personType === 'Subcontractor' ? 'Subcontractor' : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const ownershipHistory = record?.review?.ownershipHistory || []
  const personName = (snapshot, user) =>
    snapshot?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    'Unknown'

  return (
    <Stack
      gap={1}
      padding={1}
      component={Paper}
      border="solid 2px"
      sx={{ borderColor: 'contrast.main', position: 'sticky', top: 0 }}
      zIndex={100}
    >
      <Stack
        direction="row"
        gap={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography>{`${
          record?.demographics?.lastName
            ? record?.demographics?.lastName + ','
            : ''
        } ${
          record?.demographics?.firstName
            ? record?.demographics?.firstName?.[0]
            : ''
        } ${
          record?.demographics?.dob
            ? '(' + Age(record.demographics.dob, record?.createdAt) + ')'
            : ''
        }`}</Typography>
        <Chip
          color="anMainBlue"
          size="small"
          sx={{ borderRadius: 1 }}
          label={record?.epcrNumber}
        />
        <Stack gap={1} direction="row" alignItems="center">
          <Tooltip
            title={
              <Stack alignItems="center">
                <Typography variant="caption">Completed By</Typography>
                <Typography fontWeight="bold" sx={{ color: 'contrast.light' }}>
                  {completedByName}
                </Typography>
                {completedByDetail && (
                  <Typography variant="caption">{completedByDetail}</Typography>
                )}
              </Stack>
            }
          >
            <Avatar
              src={record?.review?.completedBy?.photograph}
              sx={{ borderRadius: 1, width: 24, height: 24 }}
            />
          </Tooltip>
          <Tooltip
            title={
              <Stack alignItems="center">
                <Typography variant="caption">
                  {treatmentLocation ? 'Treatment Centre' : 'Current Author'}
                </Typography>
                <Typography fontWeight="bold" sx={{ color: 'contrast.light' }}>
                  {currentHolderName || 'No current owner'}
                </Typography>
              </Stack>
            }
          >
            <Avatar
              src={currentAuthor?.photograph}
              sx={{ borderRadius: 1, width: 24, height: 24 }}
            >
              {treatmentLocation && <LocalHospitalIcon fontSize="small" />}
            </Avatar>
          </Tooltip>
          <Tooltip title="Show Record Dates/Times">
            <IconButton
              size="small"
              onClick={() => setOpenTimeInfo(true)}
              sx={{ width: 24, height: 24 }}
            >
              <Info color="anBlue" size="inherit" />
            </IconButton>
          </Tooltip>

          <Chip
            sx={{ borderRadius: 1 }}
            color={
              record?.review?.status === 'Draft'
                ? 'error'
                : record?.review?.status === 'Submitted'
                  ? 'success'
                  : 'info'
            }
            label={record?.review?.status}
            size="small"
          />
          {handlePDFDownload && (
            <MoreVertIcon
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Stack>
      </Stack>
      <BigModal
        autoWidth
        open={openTimeInfo}
        onClose={() => setOpenTimeInfo(false)}
      >
        <Stack gap={1} divider={<Divider />}>
          <Stack gap={1}>
            <Typography variant="title3">Record Created</Typography>
            <DetailRow
              label="When"
              value={moment(record.createdAt).format('DD/MM/YYYY [-] HH:mm')}
            />
            <DetailRow
              label="By"
              value={
                completedByDetail
                  ? `${completedByName} (${completedByDetail})`
                  : completedByName
              }
            />
          </Stack>
          {ownershipHistory.length > 0 && (
            <Stack gap={1}>
              <Typography variant="title3">Ownership History</Typography>
              {ownershipHistory.map((entry, index) => (
                <Stack key={index}>
                  <DetailRow
                    label={moment(entry.at).format('DD/MM/YYYY [-] HH:mm')}
                    value={`${personName(entry.fromRecord)} → ${personName(
                      entry.toRecord,
                    )}`}
                  />
                  <Typography variant="caption">
                    {[
                      entry.managerTransfer
                        ? `Manager transfer by ${personName(null, entry.by)}`
                        : `Transferred by ${personName(null, entry.by)}`,
                      entry.reason,
                    ]
                      .filter(Boolean)
                      .join(' — ')}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
          <Stack gap={1}>
            <Typography variant="title3">Current State</Typography>
            <DetailRow label="Status" value={record?.review?.status} />
            <DetailRow
              label="Record Owner"
              value={currentHolderName || 'No current owner'}
            />
          </Stack>
          <DetailRow
            label="Submitted At"
            value={
              record?.review?.submittedTime
                ? moment(record.review.submittedTime).format(
                    'DD/MM/YYYY [-] HH:mm',
                  )
                : 'Not Yet Submitted'
            }
          />
          <Stack>
            <DetailRow
              label="Record Last Updated"
              value={moment(record.updatedAt).format('DD/MM/YYYY [-] HH:mm')}
            />
            <Typography variant="caption">
              Last Updated Time could include changes made to the record after
              submission.
            </Typography>
          </Stack>
          <Button
            fullWidth
            color="anRed"
            onClick={() => setOpenTimeInfo(false)}
          >
            Close
          </Button>
        </Stack>
      </BigModal>
      <Menu
        open={!!menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
        anchorEl={menuAnchorEl}
      >
       
        <MenuItem onClick={() => setOpenReassign(true)}>
          Reassign Record
        </MenuItem>
        <MenuItem onClick={handlePDFDownload}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            justifyContent="space-between"
            width="100%"
          >
            <Typography>Download PDF</Typography>
            <UpgradeLock requiredTier={2} />
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handlePDFDownload({ anonymised: true })}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            justifyContent="space-between"
            width="100%"
          >
            <Typography>Anonymised PDF</Typography>
            <UpgradeLock requiredTier={2} />
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null)
            setOpenRaiseAudit(true)
          }}
        >
          Create Audit
        </MenuItem>
         <MenuItem
          onClick={() => {
            setMenuAnchorEl(null)
            setShowAccessLogs(true)
          }}
        >
          Access log
        </MenuItem>
        <MenuItem onClick={() => setOpenDelete(true)}>Delete Record</MenuItem>
      </Menu>

      <AccessLogs
        epcrID={record._id}
        recordLabel={record?.epcrNumber}
        open={showAccessLogs}
        onClose={() => setShowAccessLogs(false)}
      />

      <DeleteRecord
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        recordID={record._id}
      />

      <ReassignRecord
        open={openReassign}
        onClose={() => setOpenReassign(false)}
        recordID={record._id}
        loadRecord={loadRecord}
        setMenuAnchorEl={setMenuAnchorEl}
      />

      <RaiseAuditDialog
        open={openRaiseAudit}
        onClose={() => setOpenRaiseAudit(false)}
        onRaised={() => setOpenRaiseAudit(false)}
        record={{ _id: record._id, epcrNumber: record.epcrNumber }}
      />
    </Stack>
  )
}

export default Header
